import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { MAIN_URL } from "../Configs/Urls";

// Module-level cache + in-flight dedup, shared by every component on the page that calls
// useDisplayCurrency() — without this, each ResultBar/Filter/Calendar/Popup instance fired its
// own independent GET to `global/currency-rates/convert` for the exact same dealFareCurrency->vc
// pair; a slower/lost request on one instance left it silently stuck on the un-converted
// dealFareCurrency (visually indistinguishable from a real currency when the symbols collide,
// e.g. AUD's "$" vs USD's "$") while other instances already showed the converted value.
const rateCache = {}; // "FROM->TO" -> resolved rate | null (null = lookup failed)
const ratePromises = {}; // "FROM->TO" -> in-flight Promise<rate|null>

function fetchRate(from, to) {
  const key = `${from}->${to}`;
  if (rateCache[key] !== undefined) {
    return Promise.resolve(rateCache[key]);
  }
  if (ratePromises[key]) {
    return ratePromises[key];
  }
  ratePromises[key] = axios
    .get(`${MAIN_URL}global/currency-rates/convert`, { params: { base: from, to, amount: 1 } })
    .then((res) => {
      const fetchedRate = res?.data?.data?.rate;
      const resolved = fetchedRate && fetchedRate > 0 ? fetchedRate : null;
      rateCache[key] = resolved;
      return resolved;
    })
    .catch((err) => {
      console.error("Failed to fetch display-currency conversion rate:", err);
      rateCache[key] = null;
      return null;
    })
    .finally(() => {
      delete ratePromises[key];
    });
  return ratePromises[key];
}

/**
 * Display-only currency conversion for the results page — backend already prices everything in
 * the search's DealFareCurrency (the switched pipeline's own currency, e.g. AUD); this hook
 * compares that against &vc= (the currency picked on the homepage, carried in the results URL —
 * see HomePage-Flights-SLICE.js) and, if they differ, fetches just the conversion RATE from the
 * backend (`global/currency-rates/convert`) and applies it client-side wherever a price is
 * rendered. Backend data/state is never touched — this only affects what's shown on screen.
 *
 * `isConverting` is true only for the brief window a real vc!=dealFareCurrency rate lookup is
 * in flight and not yet cached — callers should show a loading placeholder instead of a price
 * during that window, rather than flashing the wrong (dealFareCurrency) value first.
 */
export function useDisplayCurrency() {
  const dealFareCurrency =
    useSelector(
      (state) => state.ResultPageFlightsFlight?.SearchResults?.DealFareCurrency
    ) || "INR";

  const vc = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlVc = urlParams.get("vc");
    if (urlVc) return urlVc.toUpperCase();

    // No &vc= in the URL — there's no results-page search yet (e.g. the homepage's own fare
    // calendar), so fall back to whatever currency the user picked in the homepage's Currency
    // dropdown (same localStorage key HomePage-Flights-SLICE.js reads via
    // getSelectedCurrencyForUrl() to build &vc= for the actual search). Without this, the
    // homepage calendar always showed raw INR regardless of the picked currency, since vc and
    // dealFareCurrency (defaulted 'INR' — no Redux SearchResults yet either) were both 'INR'.
    try {
      const stored = JSON.parse(localStorage.getItem("selectedCurrency"));
      if (stored) return String(stored).toUpperCase();
    } catch (err) {
      // Malformed/missing — fall through to dealFareCurrency below.
    }

    return dealFareCurrency.toUpperCase();
  }, [dealFareCurrency]);

  const needsConversion = !!dealFareCurrency && vc !== dealFareCurrency;
  const cacheKey = `${dealFareCurrency}->${vc}`;
  // Lazy-init so a value already cached by another component doesn't cause a loading flash here.
  const [rate, setRate] = useState(() => (needsConversion && rateCache[cacheKey]) || 1);
  // Tracks whether `rate` actually reflects a successful vc-conversion (vs. just being the
  // untouched default 1) — rate alone can't be used for that, since a real dealFareCurrency->vc
  // rate could itself legitimately be 1.0 (e.g. two currencies pegged 1:1).
  const [rateResolvedFor, setRateResolvedFor] = useState(() =>
    needsConversion && rateCache[cacheKey] ? vc : null
  );
  const [isConverting, setIsConverting] = useState(
    () => needsConversion && rateCache[cacheKey] === undefined
  );

  useEffect(() => {
    if (!needsConversion) {
      setRate(1);
      setRateResolvedFor(null);
      setIsConverting(false);
      return;
    }

    if (rateCache[cacheKey] !== undefined) {
      // Already resolved (by this or another component) — apply synchronously, no loading state.
      const resolved = rateCache[cacheKey];
      setRate(resolved || 1);
      setRateResolvedFor(resolved ? vc : null);
      setIsConverting(false);
      return;
    }

    let cancelled = false;
    setIsConverting(true);
    fetchRate(dealFareCurrency, vc).then((resolved) => {
      if (cancelled) return;
      setIsConverting(false);
      if (resolved) {
        setRate(resolved);
        setRateResolvedFor(vc);
      } else {
        setRate(1);
        setRateResolvedFor(null);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [dealFareCurrency, vc]);

  // Falls back to dealFareCurrency whenever no successful vc-conversion is actually in effect
  // (vc matches already, or the rate fetch hasn't resolved/failed) — never silently label a
  // value with a currency it isn't really in.
  const displayCurrency = rateResolvedFor === vc ? vc : dealFareCurrency;

  const convert = (amount) => {
    const num = Number(amount) || 0;
    return Math.round(num * rate * 100) / 100;
  };

  return { dealFareCurrency, vc, rate, displayCurrency, convert, isConverting };
}
